export type VisualFlags = {
  hasWaterUnlock: boolean;
  hasLandmass: boolean;
  hasContinent: boolean;
  hasRoadNetwork: boolean;
  hasRoadNetworkUpgrade: boolean;
  hasEarlySettlement: boolean;
  hasMigration: boolean;
  hasSettlementTrading: boolean;
  hasTown: boolean;
  hasCity: boolean;
  hasInfrastructure: boolean;
  hasSatellite: boolean;
  hasSpacePort: boolean;
  hasLaunchTower: boolean;
  hasRocket: boolean;
  hasOrbitalDock: boolean;
  hasProbe: boolean;
  hasDefenceSatellites: boolean;
  hasShieldSatellites: boolean;
  hasOceanRipples: boolean;
  hasCometEvent: boolean;
  hasMeteorImpact: boolean;
  hasSolarFlare: boolean;
  hasPandemic: boolean;
  hasPollutionBloom: boolean;
  hazardActive: boolean;
};

const WATER_NAMES = ['Ocean Patch', 'Water Ice Comet', 'Hot Spring', 'Hydrothermal Vent'];
// Starter land work shows a single small landmass; full continents are only
// revealed by the Continent / Land Reclamation projects themselves.
const LANDMASS_STARTER_NAMES = ['Coastal Land', 'Tectonic Uplift'];
const CONTINENT_NAMES = ['Continent', 'Land Reclamation'];
const LAND_NAMES = [...LANDMASS_STARTER_NAMES, ...CONTINENT_NAMES];
const EARLY_SETTLEMENT_NAMES = ['Early Tribe', 'Early Settlement'];
const MIGRATION_NAMES = ['Migration'];
const SETTLEMENT_TRADING_NAMES = ['Settlement Trading', 'Water System', 'Space Port'];
const ROAD_NAMES = ['Road Network'];
const INFRA_NAMES = ['Power Plant', 'Factory', 'Hospital', 'Research Centre', 'Radio Tower', 'Study Parent Star', 'Telescope', 'Clean Energy'];
const SPACE_PORT_NAMES = ['Space Port'];
const LAUNCH_NAMES = ['Launch Complex'];
const ROCKET_NAMES = ['Rocket Program'];
const ORBITAL_DOCK_NAMES = ['Orbital Dock'];
const PROBE_NAMES = ['Deep Space Probe'];
const DEFENCE_NAMES = ['Planetary Defence Grid', 'Orbital Defence Platform'];
const SHIELD_NAMES = ['High-Orbit Shield Satellites'];

/**
 * How many of the planet's configured continents should be visible.
 * Each completed Continent / Land Reclamation project reveals one more;
 * starter land work (Coastal Land, Tectonic Uplift) reveals the first as a
 * small landmass. `names` is per-item, so duplicates count.
 */
export function countContinentReveals(names: string[]): number {
  const full = names.filter((name) => CONTINENT_NAMES.includes(name)).length;
  const starter = names.some((name) => LANDMASS_STARTER_NAMES.includes(name)) ? 1 : 0;
  return full > 0 ? full + starter : starter;
}

export function deriveVisualFlags(
  names: string[],
  categories: string[],
  events: Array<{ type?: string; eventType?: string; severity?: string; day?: number; [k: string]: unknown }>,
  currentDay = 0
): VisualFlags {
  const nameSet = new Set(names);
  const has = (arr: string[]) => arr.some((x) => nameSet.has(x));
  const hazardActive = events.some((e) => {
    const t = String(e.type || e.eventType || '').toLowerCase();
    const s = String(e.severity || '').toLowerCase();
    return /catastrophe|eruption|impact|storm|warning/.test(t) || /high|critical/.test(s);
  });
  const eventText = events.map((e) => `${String(e.type || e.eventType || '')} ${String(e.name || '')} ${String(e.message || '')}`.toLowerCase()).join(' ');

  return {
    hasWaterUnlock: has(WATER_NAMES) || categories.includes('water') || categories.includes('plant'),
    hasLandmass: has(LAND_NAMES),
    hasContinent: has(CONTINENT_NAMES),
    hasRoadNetwork: has(EARLY_SETTLEMENT_NAMES) || has(ROAD_NAMES),
    hasRoadNetworkUpgrade: has(ROAD_NAMES),
    hasEarlySettlement: has(EARLY_SETTLEMENT_NAMES),
    hasMigration: has(MIGRATION_NAMES),
    hasSettlementTrading: has(SETTLEMENT_TRADING_NAMES),
    hasTown: has(['Town']),
    hasCity: has(['City']),
    hasInfrastructure: has(INFRA_NAMES),
    hasSatellite: has(['Satellite']),
    hasSpacePort: has(SPACE_PORT_NAMES),
    hasLaunchTower: has(LAUNCH_NAMES),
    hasRocket: has(ROCKET_NAMES),
    hasOrbitalDock: has(ORBITAL_DOCK_NAMES),
    hasProbe: has(PROBE_NAMES),
    hasDefenceSatellites: has(DEFENCE_NAMES),
    hasShieldSatellites: has(SHIELD_NAMES),
    hasOceanRipples: has(['Ocean Patch']) || has(WATER_NAMES),
    hasCometEvent: /comet/.test(eventText) || has(['Water Ice Comet']),
    hasMeteorImpact: events.some((e) => {
      const text = `${String(e.type || e.eventType || '')} ${String(e.name || '')} ${String(e.message || '')}`.toLowerCase();
      const day = Number(e.day || 0);
      return (/\bmeteor\b|\bmeteorite\b/.test(text) && currentDay - day <= 2 && currentDay >= day);
    }),
    hasSolarFlare: /solar|flare/.test(eventText),
    hasPandemic: /pandemic|disease|illness/.test(eventText),
    hasPollutionBloom: /pollution|smog|toxin/.test(eventText),
    hazardActive,
  };
}
